import React, { Component } from 'react';
import { Collapse, Flex, Heading, Icon, Spinner, Text } from 'core/components';
import { observer } from 'mobx-react';
import { TbLayoutSidebar } from 'react-icons/tb';
import { TOOL_CALL_STATUS } from 'shared/kaizen/constants';
import { ToolCallCard } from './KaizenChatSession.styles';
import { createToolResultComponent, hasToolResultView } from './ToolResultFactory';
import { KaizenToolResultConfirmActions } from './KaizenToolResultConfirmActions';

@observer
class KaizenToolResult extends Component {
  _flashTimeout = null;

  state = {
    shouldFlash: false,
    isExpanded: false,
    isApprovingTool: false
  };

  componentDidUpdate(prevProps, prevState) {
    const { tool, sessionModel } = this.props;
    const prevStatus = prevProps.tool?.status;
    const currStatus = tool?.status;
    const prevAwaitingApproval = prevProps.tool?.awaitingApproval;
    const currAwaitingApproval = tool?.awaitingApproval;

    // Flash logic
    if (
      (!prevStatus || prevStatus === 'PROCESSING') &&
      currStatus === TOOL_CALL_STATUS.SUCCESS &&
      !prevState.shouldFlash
    ) {
      clearTimeout(this._flashTimeout);
      setTimeout(() => {
        this.setState({ shouldFlash: true });
        this._flashTimeout = setTimeout(() => {
          this.setState({ shouldFlash: false });
        }, 900);
      }, 0);
    }

    // Auto-approval check when status changes to awaiting approval
    const isNowAwaitingApproval = currStatus === TOOL_CALL_STATUS.AWAITING_APPROVAL || currAwaitingApproval;
    const wasAwaitingApproval = prevStatus === TOOL_CALL_STATUS.AWAITING_APPROVAL || prevAwaitingApproval;

    if (isNowAwaitingApproval && !wasAwaitingApproval) {
      if (tool && sessionModel && this.isAutoApproved(sessionModel.id, tool.tool_name)) {
        // Auto-approve after a small delay to ensure the UI has updated
        setTimeout(() => {
          this.handleApprove(false);
        }, 100);
      }
    }
  }

  componentDidMount() {
    // Check if this tool is already auto-approved
    const { tool, sessionModel } = this.props;
    if (tool && sessionModel && this.isAutoApproved(sessionModel.id, tool.tool_name)) {
      const isAwaitingApproval = tool.status === TOOL_CALL_STATUS.AWAITING_APPROVAL || tool.awaitingApproval;
      if (isAwaitingApproval) {
        this.handleApprove(false);
      }
    }
  }

  componentWillUnmount() {
    clearTimeout(this._flashTimeout);
  }

  // Cookie helper functions
  setAutoApprovalCookie(sessionId, toolName) {
    const cookieName = `kaizen_auto_approve_${sessionId}`;
    const existingValue = this.getCookie(cookieName);
    const toolNames = existingValue ? existingValue.split(',') : [];

    if (!toolNames.includes(toolName)) {
      toolNames.push(toolName);
    }

    // Set cookie to expire at end of session
    document.cookie = `${cookieName}=${toolNames.join(',')};path=/;SameSite=Lax`;
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop().split(';').shift();
    }
    return null;
  }

  isAutoApproved(sessionId, toolName) {
    const cookieName = `kaizen_auto_approve_${sessionId}`;
    const value = this.getCookie(cookieName);
    if (value) {
      const toolNames = value.split(',');
      return toolNames.includes(toolName);
    }
    return false;
  }

  handleToggleExpanded = () => {
    this.setState((prevState) => ({
      isExpanded: !prevState.isExpanded
    }));
  };

  handleClick = () => {
    const { tool, onSelectToolResult, isCopilotMode } = this.props;
    const hasDetailView = hasToolResultView.includes(tool.tool_name);

    if (hasDetailView) {
      if (isCopilotMode) {
        // In copilot mode, toggle inline expansion
        this.handleToggleExpanded();
      } else {
        // In normal mode, open in side panel
        onSelectToolResult(tool);
      }
    }
  };

  handleApprove = (alwaysApprove = false) => {
    const { tool, sessionModel } = this.props;
    this.setState({ isApprovingTool: true });

    if (alwaysApprove) {
      this.setAutoApprovalCookie(sessionModel.id, tool.tool_name);
    }

    sessionModel
      .approveToolCall(tool.tool_tracking_id, true)
      .then(() => {
        this.setState({ isApprovingTool: false });
      })
      .catch((err) => {
        console.error('Failed to approve tool:', err);
        this.setState({ isApprovingTool: false });
      });
  };

  handleReject = () => {
    const { tool, sessionModel } = this.props;
    this.setState({ isApprovingTool: true });
    sessionModel
      .approveToolCall(tool.tool_tracking_id, false)
      .then(() => {
        this.setState({ isApprovingTool: false });
      })
      .catch((err) => {
        console.error('Failed to reject tool:', err);
        this.setState({ isApprovingTool: false });
      });
  };

  render() {
    const { tool, isCopilotMode, onDataViewCreate, onQueryError, sessionModel } = this.props;
    const { shouldFlash, isExpanded, isApprovingTool } = this.state;

    if (!tool) {
      return null;
    }

    const { title, status, awaitingApproval } = tool;
    const hasDetailView = hasToolResultView.includes(tool.tool_name);
    const isSuccess = status === TOOL_CALL_STATUS.SUCCESS;
    const hasResults = tool.raw_result && Object.keys(tool.raw_result).length > 0;
    const isInteractive = hasDetailView && isSuccess && hasResults;
    const isFailed = status === TOOL_CALL_STATUS.FAILED;
    const isRejected = status === TOOL_CALL_STATUS.USER_REJECTED;
    const isTimeout = status === TOOL_CALL_STATUS.APPROVAL_TIMEOUT;
    const isAwaitingApproval = status === TOOL_CALL_STATUS.AWAITING_APPROVAL || awaitingApproval;
    const isAutoApproved = sessionModel && this.isAutoApproved(sessionModel.id, tool.tool_name);
    const shouldShowApprovalUI = isAwaitingApproval && !isAutoApproved;

    const headerProps = {
      color: !status ? 'muted' : 'body',
      fontWeight: isInteractive ? 500 : 300
    };

    let cardClass = status ? status.toLowerCase() : '';
    if (status === TOOL_CALL_STATUS.SUCCESS && shouldFlash) {
      cardClass += ' flash';
    }

    if (isExpanded) {
      cardClass += ' expanded';
    }

    const toolResultCard = (
      <ToolCallCard
        interactive={isInteractive}
        elevation={isInteractive ? 2 : 0}
        className={cardClass}
        onClick={isInteractive ? this.handleClick : null}
      >
        <Flex alignItems="center" justifyContent="space-between">
          <Flex alignItems="center" gap={1} flex={1}>
            {!status && <Spinner size={12} />}
            {isFailed && <Icon icon="error" color="danger" />}
            {isRejected && <Icon icon="error" />}
            {isTimeout && <Icon icon="time" color="muted" />}
            {shouldShowApprovalUI && <Icon icon="pause" color="muted" />}
            {(status === TOOL_CALL_STATUS.SUCCESS || (isAwaitingApproval && isAutoApproved)) && (
              <Icon icon="tick-circle" color="success" className="success-icon" />
            )}
            <Heading level={6} m={0} {...headerProps}>
              {title}
            </Heading>
          </Flex>

          {isFailed && (
            <Text small color="danger">
              <Text fontWeight="bold">Error:</Text> Something went wrong
            </Text>
          )}

          {isRejected && <Text fontWeight="bold">Rejected: Tool execution was not approved</Text>}

          {isTimeout && <Text fontWeight="bold">Timeout: Approval request timed out</Text>}

          {status === TOOL_CALL_STATUS.SUCCESS && hasDetailView && hasResults && (
            <Flex alignItems="center" gap="6px">
              <Text muted small>
                {(() => {
                  if (isCopilotMode) {
                    return isExpanded ? 'Collapse' : 'Expand';
                  }
                  return 'View details';
                })()}
              </Text>
              <Icon
                icon={(() => {
                  if (isCopilotMode) {
                    return isExpanded ? 'caret-up' : 'caret-down';
                  }
                  return TbLayoutSidebar;
                })()}
                size={14}
                color="muted"
              />
            </Flex>
          )}
        </Flex>

        {shouldShowApprovalUI && (
          <KaizenToolResultConfirmActions
            tool={tool}
            onApprove={() => this.handleApprove(false)}
            onApproveAndAutoApprove={() => this.handleApprove(true)}
            onReject={this.handleReject}
            disabled={isApprovingTool}
          />
        )}
      </ToolCallCard>
    );

    // In copilot mode, show inline expansion
    if (isCopilotMode && hasDetailView && status === TOOL_CALL_STATUS.SUCCESS) {
      return (
        <Flex flexDirection="column" gap={0.5}>
          {toolResultCard}
          {isExpanded && (
            <Collapse isOpen={isExpanded}>
              {createToolResultComponent(tool, {
                onCloseToolResult: () => this.setState({ isExpanded: false }),
                onDataViewCreate,
                onQueryError,
                isInlineMode: true
              })}
            </Collapse>
          )}
        </Flex>
      );
    }

    // In normal mode, just show the card
    return toolResultCard;
  }
}

export default KaizenToolResult;
